// Ported from vlookup-dramatic/lib/content.ts by scripts/revamp/port-design.js
// Single source of truth for all vLookUp site content.
// Identical file is used by both the faithful and dramatic projects — the projects
// restyle this data, they do not re-copy it. Image paths point at /public/images.
// Company LinkedIn used by How it Works (mentee/mentor process) and the footer.
export const LINKEDIN_COMPANY = "https://www.linkedin.com/company/the-vlookup";
export const site = {
    name: "vLookUp",
    tagline: "Relevant connections for rising women",
    email: "support@vlookup.ai",
    // v3.0 footer description (client copy).
    description: "vLookUp is a global mentoring platform that connects women with experienced leaders across industries, career stages, and countries. Through relevant mentor matching, structured mentoring, and MentorLookUp, our SaaS mentoring platform, we help individuals and organisations create meaningful mentoring experiences that drive long-term growth.",
    nav: [
        { label: "How it Works", href: "/how-it-works" },
        { label: "Find a Mentor", href: "/find-a-mentor" },
        {
            label: "For Organisations",
            href: "/for-organisations",
            children: [
                {
                    label: "vLookUp Programmes",
                    desc: "Mentoring programmes designed and delivered for organisations & institutions",
                    href: "/for-organisations",
                },
                {
                    label: "MentorLookUp: an Enterprise Product",
                    desc: "Run mentoring on your own SaaS platform — your brand, your community",
                    href: "/mentorlookup",
                },
            ],
        },
        { label: "Resources", href: "/resources" },
        { label: "Events", href: "/events" },
    ],
    footer: {
        // Homepage QA: three columns + a utility row across those columns.
        groups: [
            {
                title: "Explore",
                links: [
                    { label: "Home", href: "/" },
                    { label: "How It Works", href: "/how-it-works" },
                    { label: "Find a Mentor", href: "/find-a-mentor" },
                    { label: "Resources", href: "/resources" },
                    { label: "Events", href: "/events" },
                ],
            },
            {
                title: "For Organisations",
                links: [
                    { label: "vLookUp Programmes", href: "/for-organisations" },
                    { label: "MentorLookUp: Enterprise Product", href: "/mentorlookup" },
                ],
            },
            {
                title: "About vLookUp",
                links: [
                    { label: "About Us", href: "/about" },
                    { label: "FAQs", href: "/faq" },
                    { label: "Contact Us", href: "/contact-us" },
                ],
            },
        ],
        utilityLinks: [
            { label: "Join Free", href: "/#join" },
            { label: "Log In", href: "/#login" },
            { label: "Privacy Policy", href: "/#privacy" },
            { label: "Terms & Conditions", href: "/#terms" },
        ],
        socials: [
            { label: "Facebook", glyph: "f", href: "#" },
            { label: "X", glyph: "𝕏", href: "#" },
            { label: "LinkedIn", glyph: "in", href: LINKEDIN_COMPANY },
            { label: "YouTube", glyph: "YT", href: "#" },
            { label: "Instagram", glyph: "IG", href: "https://www.instagram.com/vlookup.ai/" },
            { label: "TikTok", glyph: "TT", href: "#" },
            { label: "Spotify", glyph: "SP", href: "#" }, // shown when the settings API has spotify_link (old footer had it)
        ],
    },
    copyright: "© 2026 vLookUp.ai · All rights reserved.",
};
// ---- HOME --------------------------------------------------------------------
// Canonical 2026 platform figures (reconciled from the v2.0 mockups/DOCX).
export const stats = {
    mentors: "654",
    mentees: "2,500+",
    organisations: "500+",
    countries: "52",
    domains: "17",
    hours: "12,000+",
    maleMentorsPct: "15%",
};
export const heroStats = [
    { n: "654", l: "Mentors" },
    { n: "2,500+", l: "Mentees" },
    { n: "500+", l: "Organisations" },
    { n: "52", l: "Countries" },
    { n: "12,000+", l: "Mentoring Hours" },
];
export const heroCompanies = [
    "Deloitte", "Walt Disney", "Accenture", "Microsoft", "P&G",
    "Facebook", "Pernod Ricard", "Valentino", "Dell Tech",
];
export const audienceBlocks = [
    {
        variant: "mentee",
        icon: "keyperson",
        label: "Find your mentor",
        title: "Connect with a senior leader who gets your world",
        desc: "Browse 654 mentors across 17 domains, AI-matched to your goals. You choose who you connect with. Always free.",
        cta: "Find my mentor",
        href: "/find-a-mentor",
    },
    {
        variant: "mentor",
        icon: "mentoring",
        label: "Become a mentor",
        title: "Be the mentor you wish you'd had",
        desc: "Join 654 senior leaders already mentoring ambitious women. Flexible commitment, real impact. Just your story is enough.",
        cta: "Become a mentor",
        href: "/#join",
    },
    {
        variant: "enterprise",
        icon: "peopletogether",
        label: "For organisations",
        title: "A configurable mentoring product for your community",
        desc: "Launch a branded, AI-powered mentoring programme in days — not months. Fully configurable for D&I, ERGs, or company-wide mentoring at scale.",
        cta: "Explore for organisations",
        href: "/for-organisations",
    },
];
export const homeIntro = "vLookUp is a free global mentoring platform for women in the workforce or aspiring to join the workforce. We offer them access to role models and mentors to be guided at any stage of their career. We support organisations in creating more access and opportunities to retain and expand women workforce.";
export const howItWorks = [
    { n: 1, icon: "list", title: "Create your profile", desc: "Tell us your career goals, domain, and what you're looking for. Takes 5 minutes." },
    { n: 2, icon: "alliance", title: "Get matched", desc: "Our AI suggests mentors aligned to your goals. You choose who you connect with." },
    { n: 3, icon: "initiative", title: "Start mentoring", desc: "One-on-one conversations at your pace — within a focused 4-month window." },
];
export const featuredMentors = [
    { name: "Diana Lo", role: "Senior Manager", company: "Deloitte Consulting", domain: "Operations", level: "Senior Manager", available: true, img: "/revamp/images/p01.webp", linkedin: "https://www.linkedin.com/search/results/all/?keywords=Diana%20Lo" },
    { name: "Anantika Singh", role: "Senior Director, CSR", company: "Pernod Ricard India", domain: "Sustainability", level: "Director", available: false, img: "/revamp/images/p08.webp", linkedin: "https://www.linkedin.com/search/results/all/?keywords=Anantika%20Singh" },
    { name: "Shruti Paras Mehta", role: "Assistant Chief Counsel", company: "Walt Disney", domain: "Law", level: "C-Suite", available: true, img: "/revamp/images/p02.webp", linkedin: "https://www.linkedin.com/search/results/all/?keywords=Shruti%20Paras%20Mehta" },
    { name: "Cyrena Ng", role: "Regional Training Manager", company: "Valentino Singapore", domain: "Retail", level: "Senior Manager", available: true, img: "/revamp/images/p05.webp", linkedin: "https://www.linkedin.com/search/results/all/?keywords=Cyrena%20Ng" },
];
export const testimonials = [
    { quote: "Yujin helped direct me to key Sales courses and techniques. It was an absolute pleasure to have spoken to someone who has walked the path and proven that what I aspire to be is attainable.", name: "Raani Arunasalam", role: "Industry Development Manager · Straits Interactive", chip: "Mentee", img: "/revamp/images/p07.webp" },
    { quote: "Sangeetha came across as a very focused professional at the crossroads of figuring out her next career direction. She came well prepared with a sharp ask and eagerness to implement. I wish her all the very best.", name: "Soundari Mukherjea", role: "CEO · Soundbytes11", chip: "Mentor", img: "/revamp/images/p06.webp" },
    { quote: "Shingai is very discerning, considers many aspects of a situation, and implements proposed solutions. In the face of adversity, she perseveres. She has all the qualities of a future leader.", name: "Anne Lehman", role: "Founder · Anne Lehman Advisory", chip: "Mentor", img: "/revamp/images/p04.webp" },
];
export const partners = [
    "Pernod Ricard",
    "Dell Technologies",
    "Republic Polytechnic",
    "Masoom",
    "Singapore Institute of Management",
    "Procter & Gamble",
    "Nanyang Polytechnic",
    "MODEC",
    "Singapore Institute of Directors",
    "Ionic",
    "Wealth",
    "Able",
    "ESSEC Business School",
];
// ---- MENTORS PAGE ------------------------------------------------------------
export const mentorDomains = [
    "Consulting", "Culture", "Digital and Analytics", "Entrepreneurship",
    "Environment and Sustainability", "Finance", "Healthcare",
    "Internet / Technologies", "Law", "Marketing", "Media and Entertainment",
    "Not for Profit / Social", "Operations", "Retail",
];
// Mentor self-declarations, not services vLookUp offers — wording mirrors the
// live site's tooltips.
export const mentorSpecialFilters = [
    {
        label: "I offer a safe space to talk about non-clinical mental health",
        icon: "lotus",
        tone: "blue",
    },
    { label: "I am a certified coach", icon: "coaching", tone: "red" },
    {
        label: "I offer a safe space for LGBTQ discussions",
        icon: "inclusion",
        tone: "rainbow",
    },
];
export const mentors = [
    { name: "Aakanksha Sharma", role: "Associate Manager - SCM", company: "EY - Parthenon", flag: "🇮🇳", country: "India", domain: "Consulting", seniority: "Manager/Associate", available: true, img: "/revamp/images/p01.webp", bio: "Aakanksha is a supply-chain and operations professional with EY-Parthenon. She helps mentees break into consulting, build structured problem-solving skills, and grow executive presence early in their careers." },
    { name: "Aarti Sharma", role: "Commercial Leadership - India & Southwest Asia", company: "The Coca-Cola Company", flag: "🇮🇳", country: "India", domain: "Consulting", seniority: "Director/Head", available: false, img: "/revamp/images/p08.webp", bio: "Aarti leads commercial strategy across India and Southwest Asia at The Coca-Cola Company. She mentors women navigating commercial and general-management tracks in large multinational organisations." },
    { name: "Aastha Kapoor", role: "Product Supply Manager", company: "Procter & Gamble", flag: "🇮🇳", country: "India", domain: "Operations", seniority: "Manager/Associate", available: true, img: "/revamp/images/p03.webp", bio: "Aastha manages product supply at P&G. She supports mentees interested in operations, manufacturing and end-to-end supply-chain leadership roles." },
    { name: "Adam Moszczynski", role: "Founder, Fractional CTO", company: "Lunaori Pte Ltd", flag: "🇸🇬", country: "Singapore", domain: "Consulting", seniority: "Founder", available: true, img: "/revamp/images/p11.webp", bio: "Adam is a founder and fractional CTO who partners with early-stage teams. He mentors on technology strategy, building first engineering teams, and the founder journey." },
    { name: "Aditi Jain", role: "Business Manager - Perm IT", company: "Adecco", flag: "🇸🇬", country: "Singapore", domain: "Human Resources", seniority: "Manager/Associate", available: true, img: "/revamp/images/p05.webp", bio: "Aditi works in permanent IT recruitment at Adecco. She helps mentees with career transitions, interview preparation and positioning themselves for the roles they want." },
    { name: "Ahana Mullick", role: "Head, returning after a career break", company: "20+ years in Corporate roles", flag: "🇸🇬", country: "Singapore", domain: "Consulting", seniority: "Director/Head", available: true, img: "/revamp/images/p09.webp", bio: "Ahana is returning to the workforce after 20+ years in senior corporate roles. She is a champion for women navigating career breaks, restarts and confident re-entry." },
    { name: "Ai Ling Chan", role: "Client Executive - FSI", company: "Dell Technologies", flag: "🇸🇬", country: "Singapore", domain: "Digital and Analytics", seniority: "Manager/Associate", available: true, img: "/revamp/images/p02.webp", bio: "Ai Ling is a client executive for financial-services clients at Dell Technologies. She mentors women in enterprise tech sales, account leadership and digital transformation." },
    { name: "Aishwarya Ramakrishnan", role: "Director, Corporate Banking Technology", company: "Banking and Financial Services", flag: "🇸🇬", country: "Singapore", domain: "Internet / Technologies", seniority: "Director/Head", available: true, img: "/revamp/images/p10.webp", bio: "Aishwarya directs corporate-banking technology programmes. She supports women growing into senior technology-leadership roles within financial services." },
    { name: "Aiswarya Mahadevan", role: "Demand Planning Manager", company: "Procter & Gamble", flag: "🇸🇬", country: "Singapore", domain: "Operations", seniority: "Manager/Associate", available: false, img: "/revamp/images/p07.webp", bio: "Aiswarya leads demand planning at P&G. She mentors on analytical operations roles, cross-functional influence and stepping into people-management." },
    { name: "Alex Ribeiro", role: "Enterprise Solutions Advisor", company: "TeamForm", flag: "🇺🇸", country: "United States", domain: "Consulting", seniority: "Manager/Associate", available: true, img: "/revamp/images/p12.webp", bio: "Alex advises enterprises on operating-model and org design at TeamForm. He mentors on consulting, stakeholder management and scaling ways of working." },
    { name: "Alexander Clark", role: "Head of International APAC", company: "Behavioural Insights Team", flag: "🇸🇬", country: "Singapore", domain: "Not for Profit / Social", seniority: "Director/Head", available: true, img: "/revamp/images/p11.webp", bio: "Alexander leads APAC for the Behavioural Insights Team. He supports mentees interested in social impact, public policy and applying behavioural science to real problems." },
    { name: "Alexis Oh", role: "Regional Communications Manager & Inclusion Diversity Champion", company: "Vopak Asia", flag: "🇸🇬", country: "Singapore", domain: "Human Resources", seniority: "Manager/Associate", available: true, img: "/revamp/images/p06.webp", bio: "Alexis leads regional communications and D&I at Vopak Asia. She mentors women in communications, employer branding and building inclusive workplace cultures." },
];
export const mentorSortOptions = ["Mentor (A-Z)", "Mentor (Z-A)", "Newest First"];
export const mentorAvailabilityOptions = ["All Mentors", "Available", "Unavailable"];
export const mentorsHeroMini = [
    { name: "Aakanksha S.", company: "EY Parthenon", img: "/revamp/images/p01.webp" },
    { name: "Alexis Oh", company: "Vopak Asia", img: "/revamp/images/p06.webp" },
    { name: "Aarti Sharma", company: "Coca-Cola", img: "/revamp/images/p08.webp" },
];
// ---- RESOURCES ---------------------------------------------------------------
export const resourceTabs = [
    { label: "All posts", icon: "grid" },
    { label: "People Stories", icon: "community" },
    { label: "Points of View", icon: "idea" },
    { label: "Toolkits", icon: "toolkit" },
    { label: "Newsletter", icon: "mail" },
    { label: "Videos", icon: "play" },
];
export const featuredArticle = {
    overline: "Featured · Program Update",
    chip: "Program",
    title: "IGNITE Mentoring Program: Strengthening Women's Leadership Across Indonesia & The Region",
    excerpt: "A structured mentorship programme designed to support mid-career growth and leadership readiness — connecting aspiring women leaders with senior mentors across Southeast Asia.",
    author: "vLookUp Team",
    date: "23 February 2026",
    img: "/revamp/images/r-program.webp",
};
export const articles = [
    { chip: "Point of View", tone: "blue", title: "Financial Fitness: Money, Mindset, and Long-Term Confidence", author: "Berny Lobo", date: "14 May 2026", img: "/revamp/images/r-finance.webp", stage: "Mid Career" },
    { chip: "People Story", tone: "red", title: "Diksha Mentoring Program: Building Confident Women Leaders at Pernod Ricard India", author: "vLookUp Team", date: "29 Jan 2026", img: "/revamp/images/r-program.webp", stage: "Leadership" },
    { chip: "Program", tone: "navy", title: "vElevate: Your Support. Her Opportunity.", author: "vLookUp Team", date: "05 Jan 2026", img: "/revamp/images/r-leadership.webp", stage: "Early Career" },
    { chip: "Point of View", tone: "blue", title: "Strengthening Women's Leadership Pipelines: What It Really Takes", author: "Berny Lobo", date: "09 Dec 2025", img: "/revamp/images/r-leadership.webp", stage: "Leadership" },
    { chip: "Point of View", tone: "blue", title: "The Dark Matter of Organizations: Culture, AI & The Future of Work", author: "vLookUp Team", date: "30 Oct 2025", img: "/revamp/images/r-ai.webp", stage: "Leadership" },
    { chip: "Toolkit", tone: "green", title: "From Self-Doubt to Success: Healing Your Inner Self for Career Advancement", author: "Berny Lobo", date: "13 Aug 2025", img: "/revamp/images/r-wellbeing.webp", stage: "Early Career" },
    { chip: "Career Codebreaker", tone: "red", title: "Career Codebreaker: Ashmi Ahluwalia — Mentor & Intuitive Coach", author: "Berny Lobo", date: "22 Jul 2025", img: "/revamp/images/r-mentorstory.webp", stage: "Mid Career" },
    { chip: "Toolkit", tone: "green", title: "Toolkit for Career Evolution", author: "Berny Lobo", date: "23 Jun 2025", img: "/revamp/images/r-toolkit.webp", stage: "Mid Career" },
    { chip: "Toolkit", tone: "green", title: "Toolkit for Personal Power & Confidence", author: "Berny Lobo", date: "23 Jun 2025", img: "/revamp/images/r-toolkit.webp", stage: "Early Career" },
    { chip: "Toolkit", tone: "green", title: "Toolkit for Real-World Leadership", author: "Berny Lobo", date: "21 Jun 2025", img: "/revamp/images/r-leadership.webp", stage: "Leadership" },
    { chip: "Career Codebreaker", tone: "red", title: "Career Codebreaker: Rajesh Achanta — Senior Advisor, Supply Chain Leader", author: "Berny Lobo", date: "13 Jun 2025", img: "/revamp/images/r-mentorstory.webp", stage: "Mid Career" },
    { chip: "People Story", tone: "red", title: "Growth Through Guidance: Neha Joshi & Ann Mehers on vLookUp", author: "vLookUp Team", date: "07 May 2025", img: "/revamp/images/r-newsletter.webp", stage: "Early Career" },
];
// ---- EVENTS ------------------------------------------------------------------
export const upcomingEvent = {
    badge: "Upcoming",
    brandName: "SOCIAL BENGALURU",
    brandTagline: "Bringing together the people who make the vLookUp community what it is.",
    title: "Shared Tables. Shared Stories.",
    time: "08 July 2026, 6:00 PM – 8:00 PM (IST)",
    location: "Bangalore — Venue details to be shared shortly",
    desc: "Every day, members of the vLookUp community connect through mentoring conversations, shared experiences, and a common desire to learn and grow. The vLookUp Social: Bengaluru is a chance to bring some of those connections offline — an informal evening of conversation, networking, and connection.",
    img: "/revamp/images/e-social.webp",
};
export const pastEvents = [
    { title: "The vLookUp Social: Singapore", time: "16 April 2026, 6:00–9:00 PM SGT", location: "Level 32, IOI Central Boulevard, Singapore", img: "/revamp/images/e-social.webp" },
    { title: "Financial Fitness: Getting Started", time: "26 March 2026, 6:30–8:00 PM SGT", location: "Zoom (Virtual)", img: "/revamp/images/r-finance.webp" },
    { title: "PlayPan's International Women's Day", time: "05 March 2026, 4:00–6:00 PM HKT", location: "Glass Dome, 28 China Street, Singapore", img: "/revamp/images/g-graduation.webp" },
    { title: "Next Horizons: A Celebration of Women Rising in Tech", time: "10 December 2025, 3:00–6:00 PM SGT", location: "Dell Office, Changi Business Park, Singapore", img: "/revamp/images/r-ai.webp" },
    { title: "EmpowerHer Elevate to Unlock Your Potential", time: "06 December 2025, 2:00–3:00 PM SGT", location: "ICAI Singapore Chapter", img: "/revamp/images/r-leadership.webp" },
    { title: "Campus to Corporate: Mastering Mindset & Skills for Professional Success", time: "08 October 2025, 8:30–10:00 PM SGT", location: "Virtual via Zoom", img: "/revamp/images/e-virtual.webp" },
    { title: "IGNITE and vLookUp Collaboration", time: "24 October 2024, 3:00–5:00 PM WIB", location: "Shangri-La, Jakarta, Indonesia", img: "/revamp/images/r-program.webp" },
    { title: "SGWIT and vLookUp Relaunch", time: "18 November 2024, 10:00–11:00 AM SGT", location: "Marriott Tang Plaza Hotel, Singapore", img: "/revamp/images/g-cohort.webp" },
    { title: "vLookUp's 3rd Birthday Bash", time: "30 September 2024, 6:30–8:30 PM SGT", location: "One Raffles Quay, Singapore", img: "/revamp/images/g-graduation.webp" },
    { title: "Mentoring Programme for Republic Polytechnic", time: "30 October 2023, 7:00–8:00 PM SGT", location: "Virtual via Zoom", img: "/revamp/images/e-virtual.webp" },
    { title: "Coffee Catch-ups with Kanchana Gupta", time: "20 March 2024, 8:00–9:00 AM SGT", location: "The Dempsey Project, Singapore", img: "/revamp/images/r-mentorstory.webp" },
    { title: "Furthering Women's Empowerment — The NUS MBA & WB", time: "08 March 2024, 7:00–9:30 PM SGT", location: "NUS HSS Auditorium, Singapore", img: "/revamp/images/g-programme.webp" },
];
// ---- FOR ORGANISATIONS -------------------------------------------------------
export const forOrgHero = {
    eyebrow: "For Organisations",
    title: "Bespoke mentoring programmes for women who lead.",
    subtitle: "We design and deliver structured mentoring programmes for organisations investing in women — from D&I initiatives and ERGs to institutional and community programmes across Asia Pacific and beyond.",
    quote: "The programme created a genuine shift in how our women leaders see themselves. The quality of mentors and the structure made all the difference.",
    quoteAttr: "D&I Lead, Pernod Ricard India · Diksha Programme Sponsor",
    quoteImg: "/revamp/images/p08.webp",
    stats: [
        { n: "500+", l: "Organisations" },
        { n: "52", l: "Countries" },
        { n: "12,000+", l: "Mentoring hours" },
    ],
    trustedBy: ["Pernod Ricard", "Dell Technologies", "MODEC", "P&G"],
};
export const forOrgOffers = [
    { icon: "development", title: "Bespoke Programme Design", desc: "Co-designed around your organisation's goals, culture, and participant profile. No templates — every programme is built from the ground up." },
    { icon: "worldwide", title: "Global Mentor Network", desc: "700 vetted mentors across 50+ countries and 17+ domains — from Tech to Finance to Social Impact." },
    { icon: "alliance", title: "Smart Mentor Matching", desc: "We match on experience, domain, goals, and personality — not just seniority." },
    { icon: "settings", title: "Full Programme Management", desc: "Onboarding, kick-off sessions, check-ins, and programme close-out — handled end to end." },
    { icon: "creative", title: "Branded Experience", desc: "The programme looks and feels like yours — branded comms, cohort naming, and more." },
    { icon: "indicator", title: "Impact Reporting", desc: "Stakeholder-ready reports at programme close: participation, satisfaction scores, goal attainment." },
];
export const forOrgProcess = [
    { n: 1, title: "Discovery Call", desc: "We learn your goals, participant profile, timeline, and budget. No obligation." },
    { n: 2, title: "Programme Design", desc: "We propose a tailored programme structure, cohort size, mentor mix, and timeline." },
    { n: 3, title: "Delivery", desc: "We manage matching, onboarding, sessions, and ongoing check-ins end to end." },
    { n: 4, title: "Impact Report", desc: "You receive a full programme report — data, stories, and recommendations for next steps." },
];
export const forOrgTracks = [
    { title: "Mid-to-Senior Women", sub: "For organisations supporting women as they step into bigger roles, build leadership capability and prepare for what’s next.", label: "Programmes", chips: ["HERIZON (MODEC)", "Diksha × Pernod Ricard", "Dell Technologies", "IGNITE", "Unioleo", "Ionic Wealth", "Aibel"] },
    { title: "Early-Career Women", sub: "For academic institutions, NGOs, and foundations building confident, career-ready women from day one.", label: "Programmes", chips: ["Olay STEM", "vElevate", "Nanyang Polytechnic", "Republic Polytechnic", "WITCircle"] },
];
export const forOrgProgrammes = [
    { name: "HERizon", org: "MODEC", logo: "/revamp/images/brands/modec.png?v=8" },
    { name: "Diksha", org: "Pernod Ricard India", logo: "/revamp/images/brands/pernod-ricard.png?v=6" },
    { name: "IGNITE Women's Network", org: "IGNITE Women Network", logo: "/revamp/images/brands/ignite.png?v=6" },
    {
        name: "Mentor Enablement Workshops", org: "LEGO · Pernod Ricard India", logos: [
            { src: "/revamp/images/brands/lego.svg", alt: "LEGO" },
            { src: "/revamp/images/brands/pernod-ricard.png?v=6", alt: "Pernod Ricard India" },
        ]
    },
    {
        name: "WITCircle", org: "SCS-WIT", logos: [
            { src: "/revamp/images/brands/scs.png?v=4", alt: "Singapore Computer Society" },
            { src: "/revamp/images/brands/wit.png?v=4", alt: "Women In Tech Chapter" },
        ]
    },
    { name: "Olay STEM", org: "Procter & Gamble", logo: "/revamp/images/brands/pg.png?v=3" },
    {
        name: "Polytechnic Programmes", org: "Nanyang Polytechnic · Republic Polytechnic", splitLogos: true, logos: [
            { src: "/revamp/images/brands/nyp.png?v=3", alt: "Nanyang Polytechnic" },
            { src: "/revamp/images/brands/republic-polytechnic.jpg", alt: "Republic Polytechnic" },
        ]
    },
    { name: "vElevate", org: "vLookUp Foundation", logo: "/revamp/images/vlookup-logo.svg" },
];
export const forOrgWhy = [
    { n: "01", title: "Built exclusively for women", desc: "Five years of focus means every matching algorithm and programme framework is designed with women's career growth in mind." },
    { n: "02", title: "Real mentors, not just a directory", desc: "Every mentor is personally vetted — you get a curated network, not a search box." },
    { n: "03", title: "End-to-end delivery", desc: "We manage onboarding, check-ins, and programme close-out so your team doesn't have to." },
    { n: "04", title: "Proven across sectors", desc: "Oil & Gas, FMCG, Tech, Finance, Social Sector, Education — across Asia Pacific, India, and beyond." },
    { n: "05", title: "Flexible engagement models", desc: "Fully delivered, co-facilitated, or institution-led with our support — your choice." },
    { n: "06", title: "Measurable impact reporting", desc: "Reports your leadership team can act on — participation rates, satisfaction, goal attainment." },
];
export const forOrgGallery = [
    // { caption: "Grateful for guidance — vLookUp community event", img: "/revamp/images/fo-gallery-1.webp" },
    // { caption: "Power Hour at the International Women's Festival", img: "/revamp/images/fo-gallery-2.webp" },
    // { caption: "vLookUp turns 3 — mentors and community", img: "/revamp/images/fo-gallery-3.webp" },
    // { caption: "Breaking Access Barriers panel", img: "/revamp/images/fo-gallery-4.webp" },
    // { caption: "In conversation at a vLookUp event", img: "/revamp/images/fo-gallery-5.webp" },
    // { caption: "Mentors connecting at a programme gathering", img: "/revamp/images/fo-gallery-6.webp" },
    // { caption: "Community night with vLookUp members", img: "/revamp/images/fo-gallery-7.webp" },
    // { caption: "Programme partners after a session", img: "/revamp/images/fo-gallery-8.webp" },
    // { caption: "Came for the event. Stayed for the people.", img: "/revamp/images/fo-gallery-9.webp" },
    { caption: "Mentor Enablement Workshop — in conversation", img: "/revamp/images/fo-gallery-prog-1.webp" },
    { caption: "Mentor Enablement Workshop at LEGO", img: "/revamp/images/fo-gallery-prog-2.webp" },
    { caption: "HERIZON workshop — table discussion", img: "/revamp/images/fo-gallery-prog-3.webp" },
    { caption: "Working session during a programme workshop", img: "/revamp/images/fo-gallery-prog-4.webp" },
    { caption: "HERIZON by MODEC", img: "/revamp/images/fo-gallery-prog-5.webp" },
    { caption: "HERIZON cohort — enabling women's growth and leadership", img: "/revamp/images/fo-gallery-prog-6.webp" },
    { caption: "Programme gathering", img: "/revamp/images/fo-gallery-prog-7.webp" },
    { caption: "A session in progress", img: "/revamp/images/fo-gallery-prog-8.webp" },
    { caption: "Programme close — the cohort together", img: "/revamp/images/fo-gallery-prog-9.webp" },
    { caption: "After the session", img: "/revamp/images/fo-gallery-prog-10.webp" },
];
// ---- ABOUT -------------------------------------------------------------------
export const aboutHero = {
    eyebrow: "About vLookUp",
    title: "Passionate about diversity — by helping women grow and lead",
    body: "We're a community of experienced diversity champions united by one mission: nurturing more women in leadership by breaking access barriers. vLookUp connects aspiring women leaders with domain-specific, senior mentors — one conversation at a time.",
    founded: "Founded July 2021 · Headquartered in Singapore · Active across 52 countries",
    stats: [
        { n: "654", l: "Mentors" },
        { n: "2,500+", l: "Mentees" },
        { n: "500+", l: "Organisations" },
        { n: "12,000+", l: "Mentoring hours" },
    ],
};
export const aboutDream = {
    label: "Our Dream",
    heading: "Women shouldn't have to wait until mid-career to find their first mentor.",
    body: "Many of us experience having a mentor for the first time almost mid-way through our careers — realise the positive impact, and wonder why we didn't look earlier. There are fewer women in leadership, and finding the right role model outside your organisation is hard. We exist to change that: the right mentor, at the right career stage, in the right domain — so women can stay, grow, and lead.",
    points: [
        { title: "A powerful network", desc: "Cross-domain senior leaders as mentors, aspiring women leaders as mentees — across 17 industries and 52 countries." },
        { title: "1:1, AI-enabled connections", desc: "Share interests and goals, get real-time mentor recommendations from an AI matching platform." },
        { title: "A flexible, 4-month window", desc: "Structured enough to drive outcomes. Flexible enough to fit your career stage and schedule." },
    ],
};
export const team = [
    { badge: "Founder", name: "Kanchana Gupta", title: "Founder & CEO, vLookUp", img: "/revamp/images/p08.webp" },
    { badge: "ANZ Partner", name: "Florence Mauwa", title: "ANZ Partner", img: "/revamp/images/p03.webp" },
    { badge: "Project Manager", name: "Kanika Gupta", title: "Project Manager", img: "/revamp/images/p05.webp" },
    { badge: "Design & Comms", name: "Annette", title: "Communication Consultant & Designer", img: "/revamp/images/p10.webp" },
];
export const faqTabs = ["General Queries", "Mentor Queries", "Mentee Queries"];
export const faqGeneral = [
    { q: "How do I sign up?", a: 'You can sign up for free at vlookup.ai. Click "Join Free" and create your profile as either a mentor or a mentee. The process takes around 5 minutes.' },
    { q: "How can I update or edit my profile?", a: "Once logged in, navigate to your profile settings. You can update your photo, bio, domain expertise, career stage, and preferences at any time." },
    { q: "Is the vLookUp community restricted to women leaders?", a: "Mentees are women leaders and aspiring women in leadership. However, mentors can be of any gender — currently around 35% of our mentors identify as men. What matters is the commitment to supporting women's professional growth." },
    { q: "Can I reach out to my mentor or mentee after the engagement ends?", a: "Yes — connections made on vLookUp are yours to maintain. Many mentors and mentees continue their relationship beyond the formal 4-month engagement period." },
    { q: "Are there any suggestions for how to start an engagement?", a: "Yes — once matched, we provide both mentors and mentees with a starter guide including conversation frameworks, goal-setting templates, and suggested first-session questions to help you begin with clarity." },
    { q: "Is face-to-face mentoring supported, or is it all online?", a: "The platform is primarily designed for online engagement via video, phone, or messaging. Face-to-face sessions are at the discretion of the mentor and mentee. You can use any video platform you prefer — Zoom, Teams, Google Meet, etc." },
    { q: "How can I provide feedback on a mentoring engagement?", a: "Feedback can be submitted through the platform after each session. Feedback is visible only to the vLookUp team for quality assurance purposes — it is not shared with your mentor or mentee." },
    { q: "What can I do if I experience misconduct during an engagement?", a: "Please contact us immediately at support@vlookup.ai. We take all reports seriously and have a clear process for reviewing and acting on misconduct concerns. You can end an engagement at any time through the platform." },
];
export const faqMentor = [
    { q: "Who can be a mentor on vLookUp?", a: "Any experienced professional — of any gender — with at least 8–10 years of career experience who is committed to supporting women's career growth. We have mentors from 17+ domains including Tech, Finance, Marketing, Law, Healthcare, Social Impact, and more." },
    { q: "What does it take to mentor via vLookUp?", a: "Mentors typically commit to 1–2 sessions per month over a 4-month window — roughly 8 hours total. Sessions are online and scheduled at a time that works for both parties. We provide resources and guidance to help you mentor effectively." },
    { q: "Will I be volunteering my time as a mentor?", a: "Yes — mentoring on vLookUp is a voluntary contribution. Mentors give their time because they believe in the mission. Many mentors report that it's deeply rewarding and that they gain as much as they give." },
    { q: "How are mentors matched with mentees?", a: "Our AI matching engine considers domain expertise, career stage, goals, personality preferences, and availability. Mentors can also indicate preferred mentee profiles. Mentees can indicate preferred mentors, and both parties confirm before a match is activated." },
    { q: "Can I set limits on how many mentees I take on?", a: "Yes. You can set your availability and specify how many active engagements you're comfortable managing at one time. You can pause or update your availability at any point." },
];
export const faqMentee = [
    { q: "Who can be a mentee on vLookUp?", a: "Any aspiring woman leader from any industry or domain, at any career stage, who is looking for:", bullets: ["1:1 career-specific conversations with an experienced leader", "Domain-specific guidance from outside their organisation", "Advice from men and women who champion diverse workplaces"], aAfter: "You don't need to be at a senior level — we support women from early career through to senior leadership transitions." },
    { q: "Is there a fee to join vLookUp as a mentee?", a: "Joining vLookUp as a mentee is free. There is no subscription or session fee to connect with a mentor through our platform." },
    { q: "What does it take to be mentored via vLookUp?", a: "A clear sense of what you want to achieve, a commitment to showing up for scheduled sessions, and an openness to growth. The most successful mentoring relationships are driven by the mentee — we help you get there." },
    { q: "How does the mentoring process work?", a: "After signing up, you complete a profile including your domain, career stage, and goals. Our AI matching engine then suggests compatible mentors. You can indicate preferred mentors. Once both parties confirm, you begin a flexible 4-month engagement with regular sessions." },
    { q: "Am I limited to domain-specific mentoring only?", a: "No. While vLookUp specialises in domain-specific matching, conversations can cover any professional topic — leadership, confidence, transitions, work-life balance, salary negotiation, or simply gaining a different perspective. The agenda is yours to set." },
    { q: "What happens after the 4-month engagement?", a: "You can start a new engagement with the same or a different mentor, or simply stay connected independently. Many mentees cycle through 2–3 different mentors over time as their career evolves and their goals change." },
];
// ---- MENTORLOOKUP ------------------------------------------------------------
export const mentorLookUp = {
    badge: "Enterprise Product",
    tagline: "Empower Your Community with Smart Mentoring Technology",
    desc: "A plug-and-play mentoring platform designed for universities, membership bodies, and professional organisations. Launch structured, goal-driven mentoring programmes — fully branded, rapidly deployed, without the operational burden.",
    trustedBy: [
        { name: "ESSEC Business School", src: "/revamp/images/brands/essec.webp" },
        { name: "SIM Career Connect", src: "/revamp/images/brands/sim.webp" },
        { name: "Singapore Institute of Directors", src: "/revamp/images/brands/sid.webp" },
    ],
    heroSites: [
        { name: "SID Director Mentoring", href: "https://sid.vlookup.ai/", img: "/revamp/images/mlu-sid.webp", blackBezel: true },
        { name: "Project Protégé — SIM", href: "https://sim.vlookup.ai/", img: "/revamp/images/mlu-sim.webp", objectContain: true },
        { name: "ESSEC Augment", href: "https://essec.vlookup.ai/", img: "/revamp/images/mlu-essec.webp" },
    ],
    whoFor: [
        { icon: "graduationhat", title: "Universities & Business Schools", sub: "Strengthen student-alumni connections. Support career readiness from day one.", perfectFor: ["Alumni mentoring programmes", "Student career accelerators", "Graduate transition support", "Faculty-led research mentoring"] },
        { icon: "peopletogether", title: "Membership Bodies & Associations", sub: "Deliver a standout member benefit. Deepen engagement and retention through peer mentoring.", perfectFor: ["Professional development programmes", "Member-to-member peer mentoring", "Early-career onboarding tracks", "Leadership pipeline initiatives"] },
        { icon: "ergs", title: "ERGs & Corporate Communities", sub: "White-label mentoring for your ERG or employee community, deployed on your own platform.", perfectFor: ["Women in Leadership ERGs", "D&I community programmes", "Reverse mentoring initiatives", "Cross-functional mentoring circles"] },
    ],
    features: [
        { icon: "alliance", title: "Smart Matching", desc: "Algorithm-driven matching based on skills, goals, career stage, personality, and availability — not just domain." },
        { icon: "creative", title: "Fully Branded", desc: "Your logo, your domain, your colours. Participants experience your brand — not ours. White-labelled throughout." },
        { icon: "indicator", title: "Impact Dashboards", desc: "Real-time analytics on session activity, goal completion, satisfaction, and ROI — shareable with leadership." },
        { icon: "timemanagement", title: "Seamless Scheduling", desc: "Built-in session scheduling, reminders, and calendar integration. Zero friction for mentors and mentees." },
        { icon: "configuration", title: "Effortlessly Configurable", desc: "Set your own programme structure, eligibility rules, matching criteria, and session cadence — no code needed." },
        { icon: "worldwide", title: "Globally Scalable", desc: "Cloud-based and built for scale — from 50-person cohorts to enterprise-wide programmes across multiple countries." },
    ],
    steps: [
        { n: 1, title: "Book a demo", desc: "30-minute walkthrough of the platform tailored to your use case and organisation type." },
        { n: 2, title: "Configure & brand", desc: "We set up your branded environment, configure matching rules, and import your member list." },
        { n: 3, title: "Launch", desc: "Invite participants, activate matching, and go live — typically within 2 weeks of sign-off." },
        { n: 4, title: "Measure impact", desc: "Track progress via your dashboard and receive a programme impact report at close-out." },
    ],
    reasons: [
        { n: "01", title: "Enhance community engagement", desc: "Foster deeper, more meaningful connections across your organisation or alumni network — strengthening relationships and boosting long-term engagement." },
        { n: "02", title: "Accelerate career growth", desc: "Offer structured, goal-driven mentoring that supports professional development at every stage — from student to senior leader." },
        { n: "03", title: "Power high-impact programmes", desc: "Deliver mentoring at scale with smart admin tools, configurable workflows, and built-in analytics — maximising impact with minimal operational effort." },
        { n: "04", title: "Built on real mentoring expertise", desc: "MentorLookUp brings together the technology, methodology, and expertise behind vLookUp — shaped by real mentoring programmes and the needs of organisations, mentors, and mentees." },
    ],
    glance: [
        { n: "2 wks", l: "Average time from sign-off to platform launch" },
        { n: "4.8/5", l: "Average participant satisfaction score across programmes" },
        { n: "89%", l: "Goal attainment rate across active cohorts" },
        { n: "52", l: "Countries where vLookUp matching technology is active" },
        { n: "100%", l: "White-label — your brand, your domain, your programme" },
    ],
    cases: [
        { icon: "graduationhat", org: "ESSEC Business School", programme: "ESSEC Augment Mentoring Program", desc: "Connecting ESSEC students with alumni to support career development, community building, and cross-generational mentoring — powered by MentorLookUp.", statsKey: "ESSEC", stats: [{ n: "1k+", l: "Alumni mentors" }, { n: "200+", l: "Student mentees" }, { n: "4", l: "Countries" }] },
        { icon: "graduationhat", org: "Singapore Institute of Management", programme: "SIM Career Connect — Project Protégé", desc: "A 4-month mentorship programme that lets students engage in knowledge-sharing discussions with mentors holding deep industry expertise.", statsKey: "SIM", stats: [{ n: "71", l: "Mentors" }, { n: "71", l: "Mentees" }, { n: "4+", l: "Cohorts run" }] },
        { icon: "peopletogether", org: "Singapore Institute of Directors", programme: "SID Director Mentoring", desc: "Supporting SID members' directorship journey through meaningful online mentoring connections with fellow SID members.", statsKey: "SID", stats: [] },
    ],
};
// ==== v2.0 ADDITIONS ==========================================================
// Find a Mentor — new Seniority Level filter
export const seniorityLevels = [
    "Manager/Associate", "Director/Head", "VP/SVP", "C-Suite/Partner", "Founder",
];
// Resources — career-stage tabs
export const resourceStages = ["All Resources", "Early Career", "Mid Career", "Leadership"];
// Homepage "Start here" spotlight — surfaces How It Works / Methodology / FAQ for both audiences
export const startHere = [
    { icon: "success", title: "How it works", desc: "Sign up, get matched by AI, and start a focused 4-month mentoring programme.", href: "/how-it-works", cta: "See the process" },
    { icon: "tutorial", title: "Our methodology", desc: "How our smart matching connects you on goals and outcomes — not just job titles.", href: "/how-it-works#methodology", cta: "See how matching works" },
    { icon: "question", title: "FAQ", desc: "Cost, matching time, rematching, time commitment, and who can join — answered.", href: "/how-it-works#faq", cta: "Read the FAQ" },
];
// ---- HOW IT WORKS PAGE -------------------------------------------------------
export const howItWorksHero = {
    eyebrow: "How It Works",
    title: "From sign-up to your first conversation — in three steps.",
    sub: "vLookUp uses smart AI matching to connect you with the right senior mentor for your career stage, domain, and goals. No hunting. No cold-outreach. Just the right conversation at the right time.",
    cta: "Get started free",
};
export const menteeJourney = {
    tag: "For Mentees",
    label: "Mentee Journey",
    heading: "Three steps to your first mentoring session",
    sub: "Free to join. No commitment. Start a conversation in under a week.",
    steps: [
        { n: 1, icon: "list", title: "Sign up & tell us your goals", desc: "Create your free profile. Share your career stage, domain, and what you want to achieve from mentoring.", detail: "Takes 5 minutes. You choose your goals — career progression, leadership, domain shift, confidence, or something else entirely." },
        { n: 2, icon: "tutorial", title: "Get matched by AI", desc: "Our smart matching engine surfaces mentors who match your domain, career stage, and goals — not just your title.", detail: "You receive a curated shortlist, not an overwhelming directory. Browse profiles and request your preferred mentor." },
        { n: 3, icon: "initiative", title: "Start your 4-month programme", desc: "Begin a structured 4-month mentoring window. Meet regularly, track your progress, and grow with support.", detail: "Each programme runs 4 months. You and your mentor set the cadence — typically bi-weekly 45-minute conversations." },
    ],
};
export const mentorJourney = {
    tag: "For Mentors",
    label: "Mentor Journey",
    heading: "Share your experience. Shape a career.",
    sub: "Join 654 senior women and men who volunteer as mentors. Set your own availability. Accept requests that fit your schedule.",
    steps: [
        { n: 1, icon: "resume", title: "Apply to become a mentor", desc: "Submit your profile — your domain expertise, career history, and what you enjoy helping with most.", detail: "Applications are reviewed to ensure quality and alignment with mentee needs. Most applicants receive a response within 5 business days." },
        { n: 2, icon: "choice", title: "Set your availability", desc: "Indicate how many mentees you can take on each month and your preferred session format.", detail: "You stay in control. You choose when you're open to requests and can pause anytime. Typical commitment is 2 sessions per month." },
        { n: 3, icon: "help", title: "Accept requests & begin", desc: "Receive mentee match requests. Review profiles and accept those where you can make the biggest impact.", detail: "You set the agenda together. Most mentors find the experience as valuable to them as it is to their mentees." },
    ],
};
export const methodology = {
    label: "Our Methodology",
    heading: "AI that matches on outcomes, not just keywords",
    sub: "Most platforms match on job title and industry. We match on what you actually need — your goals, your career stage, the gaps you want to close.",
    signals: [
        { icon: "success", title: "Goal-based matching", desc: "Your stated goals (leadership, domain shift, confidence, re-entry) are the primary matching signal, not your job title." },
        { icon: "worldwide", title: "Domain and geography", desc: "Matched across 17 domains and 52 countries. Cross-sector matches are common and often the most impactful." },
        { icon: "shieldcheck", title: "Availability-first", desc: "You're only matched with mentors who are currently accepting mentees. No waiting lists, no unanswered requests." },
    ],
    score: {
        title: "Match Score Breakdown",
        rows: [
            { label: "Domain alignment", pct: 92 },
            { label: "Career stage fit", pct: 87 },
            { label: "Goal alignment", pct: 95 },
            { label: "Availability", pct: 100 },
        ],
        overallLabel: "Overall Match",
        overall: "94% — Strong Match",
    },
};
export const programme = {
    label: "The 4-Month Programme",
    heading: "A structured window for real progress",
    sub: "Mentoring works best with a defined timeframe. The 4-month programme gives enough time for real change – without an open-ended commitment.",
    months: [
        { label: "Month 1 – Connect", desc: "Meet your mentor. Set goals together. Agree on a working rhythm and what success looks like for you in 4 months.", highlight: false },
        { label: "Months 2–3 – Work", desc: "Get to the core of the programme. Regular sessions with focused topics – career moves, skills, confidence, stakeholder navigation.", highlight: true },
        { label: "Month 4 – Apply & Close", desc: "Apply what you've learned. Review your progress. Close the formal programme. Many mentoring relationships continue informally.", highlight: false },
    ],
};
export const howItWorksFaqs = [
    { q: "Is vLookUp free to join?", a: "Yes. vLookUp is completely free for individual mentees and mentors. There is no subscription, no trial, no payment required. The platform is funded through enterprise partnerships with organisations running structured programmes." },
    { q: "How long does matching take?", a: "Most mentees receive a curated match recommendation within 3–5 business days of completing their profile. If your preferred mentor is unavailable, the system surfaces alternatives immediately." },
    { q: "What if I don't connect with my mentor?", a: "It happens. Let us know within the first two sessions and we'll rematch you. No questions asked. The goal is a genuinely useful relationship, not a forced one." },
    { q: "Can I do more than one programme?", a: "Yes. Many mentees complete multiple programmes over the years as their career evolves. You can also switch domains — for example, starting with a finance mentor and later requesting a leadership mentor." },
    { q: "How much time does a mentor need to commit?", a: "Typically 2 sessions per month, each around 45 minutes. Mentors set their own availability and can pause between programmes. The expected annual commitment is 8–12 hours per mentee." },
    { q: "Is vLookUp only for women?", a: "Mentees are women at any career stage. Mentors are any gender — around 15% of our mentors are men who champion women's advancement in their industries." },
];
export const howItWorksCta = {
    heading: "Ready to find your mentor?",
    sub: "Join 2,500+ women who have already started their mentoring journey. Free. No financial commitment.",
    ctas: [
        { label: "Join Free", href: "/#join" },
        { label: "Browse Mentors", href: "/find-a-mentor" },
    ],
};
// ---- OUR IMPACT: 5-year milestone timeline -----------------------------------
// v4.0 timeline (July 2026 content deck — "Our Journey / Five years of impact").
export const milestones = [
    { year: "2021", title: "vLookUp launches in Singapore", desc: "Founded by Kanchana Gupta to connect aspiring women leaders with senior mentors across domains." },
    { year: "2022", title: "First enterprise programme: HERIZON", desc: "HERIZON by MODEC launches – the first structured corporate mentoring programme, spanning Singapore and Japan." },
    { year: "2023", title: "500+ mentors · Global expansion", desc: "Programmes launched with Pernod Ricard (Diksha), Dell Technologies, and Olay STEM. Platform grows beyond Asia." },
    { year: "2024", title: "Inclusive features launched", desc: "Creating safe spaces to discuss LGBTQ topics and mental wellness during mentoring sessions with mentors who have agreed." },
    { year: "2025", title: "MentorLookUp SaaS platform", desc: "Launch of MentorLookUp – an enterprise SaaS platform enabling organisations to run their own managed mentoring programmes." },
    { year: "2026", title: "Launched vElevate", desc: "Bringing together the NGO-based mentoring initiatives under one umbrella, enabling more support for women from low-income groups." },
];
export const journeySection = {
    eyebrow: "Our Journey",
    heading: "Five years of impact",
    sub: "From a Singapore-based idea to a global mentoring platform active around the world.",
};
// ---- OUR IMPACT: "Real Voices" testimonials ----------------------------------
export const realVoices = [
    { quote: "My mentor helped me move from a mid-level role in logistics to a Director position in just 18 months. The conversations were brutally honest and exactly what I needed.", name: "Priya M.", role: "Director, Supply Chain · Singapore", img: "/revamp/images/p05.webp" },
    { quote: "I was ready to leave the workforce after my second child. vLookUp connected me with a mentor who had navigated the same crossroads. I'm now heading a team of 40.", name: "Sarah L.", role: "Head of Operations · Australia", img: "/revamp/images/p10.webp" },
    { quote: "As a mentor, I get as much as I give. Seeing my mentee land her first leadership role after six months of sessions — that's why I do this.", name: "Kavya R.", role: "VP, Financial Services · India", img: "/revamp/images/p03.webp" },
];
// ---- Homepage hero video -----------------------------------------------------
// "Watch the vLookUp story" popup. Replace `url` with the real video — a YouTube
// or Vimeo link, OR a self-hosted .mp4 (drop the file in /public and use "/xyz.mp4").
// A public sample clip is used as a placeholder so the popup is fully working now.
export const heroVideo = {
    title: "Watch the vLookUp story",
    url: "https://www.youtube.com/watch?v=kMCNm7q7CD4",
    poster: "",
};
// ==== v3.0 CLIENT-FEEDBACK REVAMP (July 2026) =================================
// Copy below comes verbatim from the client's revamp brief. Pages should prefer
// these exports; earlier blocks are kept so untouched sections keep compiling.
// ---- HOME: hero video carousel of event footage --------------------------------
export const homeHero = {
    title: "Career growth starts with access to the right conversation.",
    sub: "Built for women who are navigating what's next in their careers – from first jobs and career transitions to leadership growth. Connect with mentors who understand your industry and ambitions.",
    primaryCta: { label: "Find Your Mentor", href: "/find-a-mentor" },
    secondaryCta: { label: "Create Your Profile", href: "/#join" },
    // v4.0: the client-supplied event-footage video is the hero banner. The slides
    // below remain the prefers-reduced-motion / no-JS fallback.
    video: { src: "/revamp/videos/home-hero.mp4", poster: "/revamp/images/hero-poster.webp" },
    // Event-footage slides (crossfade + slow Ken-Burns zoom so stills read as video).
    // hero-1..5 are dedicated 1536px renders — don't swap in the smaller card images.
    slides: [
        { img: "/revamp/images/hero-1.webp", caption: "IGNITE Cohort Launch — Singapore" },
        { img: "/revamp/images/hero-2.webp", caption: "Diksha Graduation — Pernod Ricard India" },
        { img: "/revamp/images/hero-3.webp", caption: "The vLookUp Social — Bengaluru" },
        { img: "/revamp/images/hero-4.webp", caption: "HERIZON Programme — MODEC" },
        { img: "/revamp/images/hero-5.webp", caption: "Campus to Corporate — Virtual" },
    ],
    ticker: [
        { n: "654", l: "Mentors" },
        { n: "2,500+", l: "Mentees" },
        { n: "500", l: "Organisations" },
        { n: "52", l: "Countries" },
        { n: "12,000+", l: "Mentoring Hours" },
    ],
};
// ---- HOME: 4 horizontal audience cards ("What brings you here today?") ---------
export const audienceSection = {
    heading: "What brings you here today?",
    sub: "Pick your path – every journey on vLookUp starts with one click.",
    cards: [
        {
            icon: "keyperson",
            title: "Looking for a mentor who's been there before?",
            desc: "Find guidance, perspective, and support for your next step.",
            cta: "Choose a Mentor",
            href: "/find-a-mentor",
            img: "/revamp/images/home-looking-mentor.webp",
        },
        {
            icon: "mentoring",
            title: "Ready to share your experience and help a woman move forward?",
            desc: "Join a global community of mentors creating meaningful impact.",
            cta: "Become a Mentor",
            href: "/#join",
            img: "/revamp/images/home-ready-to-share.webp",
        },
        {
            icon: "peopletogether",
            title: "Investing in the growth of your women students, employees, and community?",
            desc: "Create mentoring experiences that help them learn, grow, and thrive.",
            cta: "Start a Programme",
            href: "/for-organisations",
            img: "/revamp/images/home-investing.webp",
        },
        {
            icon: "deployment",
            title: "Want to unlock the expertise within your community?",
            desc: "Turn alumni, members, and leaders into mentors through our SaaS-based platform.",
            cta: "Explore MentorLookUp",
            href: "/mentorlookup",
            img: "/revamp/images/home-unlock-expertise.webp",
        },
    ],
};
// ---- HOME: "New to vLookUp" section (retains current setup, new copy) ----------
export const howVLookUpWorks = {
    eyebrow: "How vLookUp Works",
    heading: "Everything you need to start mentoring with confidence",
    sub: "Whether you're looking for a mentor or ready to become one, explore how vLookUp creates meaningful mentoring relationships.",
    items: [
        {
            icon: "success",
            title: "The Mentoring Journey",
            desc: "Understand what to expect from sign-up to meaningful mentoring conversations.",
            cta: "Explore the journey",
            href: "/how-it-works",
        },
        {
            icon: "tutorial",
            title: "Our Matching Methodology",
            desc: "Learn how intelligent matching and mentor choice create more relevant connections.",
            cta: "Explore our methodology",
            href: "/how-it-works#methodology",
        },
        {
            icon: "question",
            title: "Frequently Asked Questions",
            desc: "Find answers about joining, matching, mentoring, and getting started.",
            cta: "Browse FAQs",
            href: "/faq",
        },
    ],
};
// ---- HOME: community section header + Instagram feed ---------------------------
export const communitySection = {
    eyebrow: "Our Global Mentoring Community",
    heading: "Explore our Diversity Champions",
    browseCta: { label: "Browse all", href: "/find-a-mentor" },
};
// v4.0 home section headings (verbatim from the July 2026 content deck).
export const partnersHeading = "Trusted by Organisations across Industries";
export const testimonialsHeading = "What our mentors and mentees are saying";
export const appSectionSub = "Manage sessions, browse mentors, and stay connected – on the go.";
// The live vLookUp Mentoring apps (VLOOKUP PTE. LTD). Both QR codes in
// /public/images encode exactly these two URLs — regenerate them if either
// link changes.
export const appStoreUrl = "https://apps.apple.com/app/vlookup-mentoring/id6443502315";
export const playStoreUrl = "https://play.google.com/store/apps/details?id=com.vlookup";
// Three plain-language capabilities, drawn from appSectionSub.
export const appSectionPoints = [
    "Browse mentors and shortlist your matches",
    "Book and reschedule sessions in a tap",
    "Browse resources and get career growth tips",
];
export const instagramFeed = {
    handle: "@vlookup.ai",
    href: "https://www.instagram.com/vlookup.ai/",
    heading: "Follow the journey on Instagram",
    posts: [
        { img: "/revamp/images/g-cohort.webp", caption: "Cohort kick-off energy ✨" },
        { img: "/revamp/images/e-social.webp", caption: "Shared tables, shared stories" },
        { img: "/revamp/images/g-graduation.webp", caption: "Celebrating our graduates 🎓" },
        { img: "/revamp/images/r-mentorstory.webp", caption: "Mentor spotlight" },
        { img: "/revamp/images/g-programme.webp", caption: "Programme day with MODEC" },
        { img: "/revamp/images/e-virtual.webp", caption: "Virtual, but make it warm" },
    ],
};
// "Be part of the family" section is renamed:
export const appSectionHeading = "Continue your mentoring journey anywhere";
// ---- HOW IT WORKS / YOUR EXPERIENCE (v3) ---------------------------------------
export const hiwHeroV3 = {
    title: "Every meaningful mentoring journey starts here.",
    sub: "Discover how we combine mentor choice, intelligent matching, and a structured mentoring journey to help every connection begin with purpose.",
    ctas: [
        { label: "Process for Mentees", href: "#mentees" },
        { label: "Process for Mentors", href: "#mentors" },
    ],
};
export const menteeFramework = {
    id: "mentees",
    tag: "For Mentees",
    heading: "The Mentee Growth Framework",
    steps: [
        { n: 1, icon: "list", title: "Define", desc: "Share your goals, career stage, interests, aspirations, and the challenges you'd like support with.", href: "/#join" },
        { n: 2, icon: "keyperson", title: "Discover", desc: "Explore mentors aligned to your domain, experience, interests, and goals. Our platform also recommends mentors who may be a strong fit.", href: "/find-a-mentor" },
        { n: 3, icon: "experience", title: "Choose", desc: "Make a selection of up to 5 mentors and the first mentor who selects your request will be assigned to you." },
        { n: 4, icon: "armwrestling", title: "Connect", desc: "Begin a flexible four-month mentoring engagement focused on your goals, supported by practical tools and structured check-ins." },
        { n: 5, icon: "initiative", title: "Grow", desc: "Apply new perspectives, build momentum, and continue learning through the wider vLookUp community.", href: LINKEDIN_COMPANY },
    ],
};
export const mentorFramework = {
    id: "mentors",
    tag: "For Mentors",
    heading: "The Mentor Journey",
    steps: [
        { n: 1, icon: "resume", title: "Create", desc: "Build your mentor profile by sharing your expertise, career journey, mentoring interests, and the women you'd like to support.", href: "/#join" },
        { n: 2, icon: "choice", title: "Set Preferences", desc: "Choose your availability, mentoring capacity, and preferred areas of guidance. You stay in control of your commitment." },
        { n: 3, icon: "help", title: "Connect", desc: "Receive mentoring requests from women who believe your experience can help them. Review each request before accepting." },
        { n: 4, icon: "mentoring", title: "Mentor", desc: "Build meaningful one-to-one mentoring relationships over four months through conversations that encourage confidence, clarity, and growth." },
        { n: 5, icon: "alliance", title: "Move Forward", desc: "Continue supporting future women leaders, while remaining part of a global community creating lasting impact.", href: LINKEDIN_COMPANY },
    ],
};
export const platformFeatures = {
    eyebrow: "The Platform",
    heading: "One platform. Every step supported.",
    sub: "From onboarding to ongoing mentoring, every part of the journey is designed to make mentoring simple, structured, and human.",
    items: [
        { icon: "resume", title: "Profile & Onboarding", desc: "Create a rich profile that powers better recommendations." },
        { icon: "alliance", title: "Smart Discovery", desc: "Browse mentors or receive personalised recommendations." },
        { icon: "timemanagement", title: "Scheduling", desc: "Coordinate meetings directly through the platform." },
        { icon: "help", title: "Secure Messaging", desc: "Stay connected before, during, and after sessions." },
        { icon: "indicator", title: "Feedback & Progress", desc: "Capture reflections and measure progress throughout the engagement." },
        { icon: "configuration", title: "Programme Management", desc: "Structured tools for individuals and organisational mentoring programmes." },
    ],
};
export const matchingPeople = {
    id: "methodology",
    eyebrow: "Matching Methodology",
    heading: "Matching built around people",
    sub: "vLookUp considers the combination of your goals, career stage, domain, mentoring interests, and availability to recommend mentors who are relevant.",
    items: [
        { icon: "success", title: "Goal Alignment", desc: "Find mentors who can help with the challenges you're solving today." },
        { icon: "worldwide", title: "Domain Expertise", desc: "Recommendations based on 17 domains and 75+ specialisations." },
        { icon: "development", title: "Career Stage", desc: "Find support, whether you're starting out, transitioning, or preparing for leadership." },
        { icon: "timemanagement", title: "Availability", desc: "Only mentors currently open to mentoring are recommended." },
    ],
};
// FAQs no longer live on this page — link out instead.
export const hiwFaqLink = {
    lead: "Have more questions?",
    cta: "Browse through our FAQs",
    href: "/faq",
};
// ---- FIND A MENTOR (v3) --------------------------------------------------------
export const findMentorHero = {
    title: "Find a mentor for your career goals.",
    sub: "Every mentor here creates a safe space for real conversations – be it career growth, leadership, confidence, workplace challenges, transitions, wellbeing, or identity. One conversation with them can change how you see your next step.",
    cta: { label: "Join as a Mentor or Mentee", href: "/#join" },
    // Mentor-specific hero stats ticker.
    ticker: [
        { n: "654", l: "Mentors" },
        { n: "17", l: "Domains" },
        { n: "30+", l: "Specialisations" },
        { n: "52", l: "Countries" },
        { n: "15%", l: "Male Allies" },
    ],
    // Right side of the banner: collage of companies mentors belong to.
    companies: [
        "Deloitte", "Walt Disney", "Accenture", "Microsoft", "P&G", "Coca-Cola",
        "Pernod Ricard", "Valentino", "Dell Technologies", "EY-Parthenon",
        "Adecco", "Vopak",
    ],
    photos: [
        { src: "/revamp/images/find-mentor-hero-1.webp", alt: "Mentee and mentor holding vLookUp signs at a community event" },
        { src: "/revamp/images/find-mentor-hero-2.webp", alt: "vLookUp mentors on stage at the International Women's Festival Power Hour" },
        { src: "/revamp/images/find-mentor-hero-3.webp", alt: "Mentor and mentee together at a vLookUp anniversary gathering" },
        { src: "/revamp/images/find-mentor-hero-4.webp", alt: "Breaking Access Barriers panel with vLookUp founder Kanchana Gupta" },
        { src: "/revamp/images/find-mentor-hero-5.webp", alt: "Speakers during a vLookUp mentoring session" },
        { src: "/revamp/images/find-mentor-hero-6.webp", alt: "Members of the vLookUp community together at an event" },
        { src: "/revamp/images/find-mentor-hero-7.webp", alt: "vLookUp mentors and mentees at a networking gathering" },
        { src: "/revamp/images/find-mentor-hero-8.webp", alt: "Two members of the vLookUp community at a workplace event" },
        { src: "/revamp/images/find-mentor-hero-9.webp", alt: "vLookUp community at an event photo frame" },
    ],
};
// Mentoring levels a mentor is open to (card facet).
export const mentoringLevels = ["Entry", "Mid-career", "Senior"];
const mentorExtras = {
    "Aakanksha Sharma": { languages: ["English", "Hindi"], openTo: ["Entry", "Mid-career"] },
    "Aarti Sharma": { languages: ["English", "Hindi"], openTo: ["Mid-career", "Senior"] },
    "Aastha Kapoor": { languages: ["English", "Hindi"], openTo: ["Entry", "Mid-career"] },
    "Adam Moszczynski": { languages: ["English", "Polish"], openTo: ["Mid-career", "Senior"] },
    "Aditi Jain": { languages: ["English", "Hindi"], openTo: ["Entry", "Mid-career"] },
    "Ahana Mullick": { languages: ["English", "Hindi", "Bengali"], openTo: ["Mid-career", "Senior"] },
    "Ai Ling Chan": { languages: ["English", "Mandarin"], openTo: ["Entry", "Mid-career"] },
    "Aishwarya Ramakrishnan": { languages: ["English", "Tamil"], openTo: ["Mid-career", "Senior"] },
    "Aiswarya Mahadevan": { languages: ["English", "Tamil", "Malayalam"], openTo: ["Entry", "Mid-career"] },
    "Alex Ribeiro": { languages: ["English", "Portuguese"], openTo: ["Mid-career"] },
    "Alexander Clark": { languages: ["English"], openTo: ["Entry", "Mid-career", "Senior"] },
    "Alexis Oh": { languages: ["English", "Mandarin", "Malay"], openTo: ["Entry", "Mid-career"] },
};
// Mentor cards now carry languages + levels they're open to mentoring.
export const mentorsV3 = mentors.map((m) => ({
    ...m,
    ...(mentorExtras[m.name] ?? { languages: ["English"], openTo: ["Entry", "Mid-career"] }),
}));
// ---- vLOOKUP PROGRAMMES (For Organisations, v3) --------------------------------
export const forOrgHeroV3 = {
    eyebrow: "vLookUp Programmes",
    title: "Mentoring programmes designed for organisations & institutions.",
    subtitle: "vLookUp helps women build confidence, navigate transitions, strengthen leadership, and unlock new opportunities. Whether you're looking to empower women graduates, support leadership development, or integrate mentorship into your DEI initiatives, we help you design and scale impactful mentoring initiatives.",
    primaryCta: { label: "Reach out Today", href: "mailto:support@vlookup.ai" },
    secondaryCta: { label: "How it Works", href: "#process" },
};
// Image carousel that sits above the testimonial card on the top banner.
export const forOrgImages = [
    { src: "/revamp/images/fo-carousel-1.webp", alt: "Women leaders at a vLookUp mentoring programme" },
    { src: "/revamp/images/fo-carousel-2.webp", alt: "Students and mentors at a vLookUp programme" },
    { src: "/revamp/images/fo-carousel-3.webp", alt: "Women leaders at a Dell Technologies programme" },
    { src: "/revamp/images/fo-carousel-4.webp", alt: "IGNITE Women's Network mentoring programme" },
];
// Testimonial carousel that sits below the image on the top banner.
export const forOrgTestimonials = [
    // {
    //     quote: "The programme created a genuine shift in how our women leaders see themselves. The quality of mentors and the structure made all the difference.",
    //     attr: "D&I Lead, Pernod Ricard India · Diksha Programme Sponsor",
    // },
    // {
    //     quote: "Our students gained mentors they could never have reached on their own — and the structure kept every pair moving forward.",
    //     attr: "Programme Lead · Republic Polytechnic",
    // },
    // {
    //     quote: "vLookUp handled everything end to end. We simply watched our women leaders grow in confidence, session after session.",
    //     attr: "ERG Sponsor · Dell Technologies",
    // },
    {
        quote: "The vLookUp team has been very open to understanding IGNITE’s objectives. Having a platform and partner that can facilitate the mentor-mentee connection has made it much easier for us to create mentoring opportunities at scale.",
        attr: "-Margareth Misyel Wongkar, IGNITE Women's Network Programme Coordinator",
    },
    {
        quote: "vLookUp's features and functionality are thoughtfully designed and highly relevant to the objectives of a mentoring programme. The vLookUp team is professional, thoughtful, and responsive to every request raised by WIT.",
        attr: "- Ai Ling Chan, Mentoring Lead, WIT-Circle; Client Executive - FSI, Dell Technologies",
    },
];
// "Programs we're proud of" — provision for a hyperlink per programme (empty = not linked yet).
export const forOrgProgrammeLinks = {
    HERizon: "https://vlookup.ai/resources-details/modec-herizon-supporting-women-leaders-in-the-energy-industry",
    Diksha: "https://vlookup.ai/resources-details/diksha-mentoring-program-building-confident-women-leaders-at-pernod-ricard-india",
    "IGNITE Women's Network": "https://vlookup.ai/resources-details/ignite-mentoring-program-strengthening-womens-leadership-across-indonesia-thailand",
    "Mentor Enablement Workshops": "https://vlookup.ai/resources-details/workshops-turn-mentoring-experience-into-mentoring-impact",
    WITCircle: "https://www.vlookup.ai/resources-details/witcircle-creating-more-pathways-for-women-in-tech",
    "Olay STEM": "https://vlookup.ai/resources-details/olay-mentoring-programme-from-stem-role-models-to-wider-access",
    "Polytechnic Programmes": "https://vlookup.ai/resources-details/polytechnics-starting-career-conversations-earlier",
    vElevate: "https://vlookup.ai/resources-details/velevate-your-support-her-opportunity",
};
// ---- MENTORLOOKUP (v3): expandable feature list, image swaps on the right ------
export const mluFeatureImages = {
    "Smart Matching": "/revamp/images/mlu-feat-smart-matching.webp",
    "Fully Branded": "/revamp/images/mlu-feat-fully-branded.webp",
    "Impact Dashboards": "/revamp/images/mlu-feat-impact-dashboards.webp",
    "Seamless Scheduling": "/revamp/images/mlu-feat-seamless-scheduling.webp",
    "Effortlessly Configurable": "/revamp/images/mlu-feat-effortlessly-configurable.webp",
    "Globally Scalable": "/revamp/images/mlu-feat-globally-scalable.webp",
};
// ---- RESOURCES (v3): Blogs / Downloads / Newsletters / Videos + blog tags ------
export const resourceSections = ["Blogs", "Downloads", "Newsletters", "Videos"];
export const blogTags = [
    "Career Growth", "Leadership", "Career Transitions", "Confidence",
    "Mentoring", "Workplace Challenges", "Return to Work", "Personal Branding",
];
const articleMeta = {
    "Financial Fitness: Money, Mindset, and Long-Term Confidence": { section: "Blogs", tags: ["Career Growth", "Confidence"] },
    "Diksha Mentoring Program: Building Confident Women Leaders at Pernod Ricard India": { section: "Blogs", tags: ["Mentoring", "Leadership"] },
    "vElevate: Your Support. Her Opportunity.": { section: "Blogs", tags: ["Mentoring", "Career Growth"] },
    "Strengthening Women's Leadership Pipelines: What It Really Takes": { section: "Blogs", tags: ["Leadership"] },
    "The Dark Matter of Organizations: Culture, AI & The Future of Work": { section: "Blogs", tags: ["Leadership", "Workplace Challenges"] },
    "From Self-Doubt to Success: Healing Your Inner Self for Career Advancement": { section: "Blogs", tags: ["Confidence", "Career Growth"] },
    "Career Codebreaker: Ashmi Ahluwalia — Mentor & Intuitive Coach": { section: "Newsletters", tags: ["Mentoring", "Personal Branding"] },
    "Toolkit for Career Evolution": { section: "Downloads", tags: ["Career Transitions"] },
    "Toolkit for Personal Power & Confidence": { section: "Downloads", tags: ["Confidence"] },
    "Toolkit for Real-World Leadership": { section: "Downloads", tags: ["Leadership"] },
    "Career Codebreaker: Rajesh Achanta — Senior Advisor, Supply Chain Leader": { section: "Newsletters", tags: ["Mentoring", "Career Growth"] },
    "Growth Through Guidance: Neha Joshi & Ann Mehers on vLookUp": { section: "Newsletters", tags: ["Mentoring"] },
};
// Extra blog posts so every tag has coverage.
const extraBlogs = [
    { chip: "Point of View", tone: "blue", title: "Returning to Work After a Career Break: A Mentor's Playbook", author: "Berny Lobo", date: "02 Jun 2026", img: "/revamp/images/r-wellbeing.webp", stage: "Mid Career", section: "Blogs", tags: ["Return to Work", "Confidence"] },
    { chip: "People Story", tone: "red", title: "Owning the Room: Personal Branding Lessons from Senior Women Leaders", author: "vLookUp Team", date: "20 Apr 2026", img: "/revamp/images/r-leadership.webp", stage: "Leadership", section: "Blogs", tags: ["Personal Branding", "Leadership"] },
    { chip: "Point of View", tone: "blue", title: "Navigating Career Transitions: When to Stay, When to Move", author: "Berny Lobo", date: "11 Mar 2026", img: "/revamp/images/r-finance.webp", stage: "Mid Career", section: "Blogs", tags: ["Career Transitions", "Workplace Challenges"] },
];
// Videos live in their own first-level section.
const resourceVideos = [
    { chip: "Video", tone: "red", title: "The vLookUp Story — with Founder Kanchana Gupta", author: "vLookUp Team", date: "12 Mar 2026", img: "/revamp/images/r-mentorstory.webp", stage: "All", section: "Videos", tags: ["Mentoring"], url: "https://www.youtube.com/watch?v=Ths3o8X-XfM" },
    { chip: "Video", tone: "red", title: "Next Horizons: Women Rising in Tech — Event Highlights", author: "vLookUp Team", date: "18 Dec 2025", img: "/revamp/images/r-ai.webp", stage: "All", section: "Videos", tags: ["Leadership"], url: "https://www.youtube.com/watch?v=kMCNm7q7CD4" },
    { chip: "Video", tone: "red", title: "EmpowerHer Elevate — Session Replay", author: "vLookUp Team", date: "10 Dec 2025", img: "/revamp/images/r-leadership.webp", stage: "All", section: "Videos", tags: ["Confidence", "Career Growth"], url: "https://www.youtube.com/watch?v=Ths3o8X-XfM" },
];
// Full v3 resource catalogue. NOTE: no "featured blog" anymore (needs constant updates).
export const resourceItems = [
    ...articles.map((a) => ({
        ...a,
        ...(articleMeta[a.title] ?? { section: "Blogs", tags: ["Mentoring"] }),
    })),
    ...extraBlogs,
    ...resourceVideos,
];
// ---- EVENTS (v3) ---------------------------------------------------------------
// Main page shows BOTH upcoming and past behind two filter tabs; past events render
// a greyed-out RSVP. The RSVP CTA must be prominent and repeated at the end.
export const eventsPageV3 = {
    heading: "vLookUp Events",
    sub: "From mentoring socials to leadership panels – join the community in person or online.",
    tabs: ["Upcoming", "Past"],
    rsvp: {
        label: "RSVP for this event",
        href: "mailto:support@vlookup.ai?subject=RSVP%20%E2%80%94%20The%20vLookUp%20Social%3A%20Bengaluru",
    },
};
// ---- ABOUT US (v3) --------------------------------------------------------------
export const aboutStory = {
    eyebrow: "The vLookUp Story",
    heading: "Mentorship shouldn't depend on privilege",
    paras: [
        "vLookUp was founded on a simple belief: career potential should be shaped by talent, effort, and ambition – not by postcode, pedigree, personal networks, or circumstance.",
        "Growing up in a small town without access to influential networks or role models, our founder, Kanchana Gupta, experienced firsthand how difficult it can be to find the guidance that helps careers grow.",
        "So in 2021, she created vLookUp.",
        "Today, vLookUp is a global mentoring platform that connects women across career stages with experienced leaders who offer perspective, support, and guidance.",
    ],
    // Right side: embedded video.
    videoUrl: "https://www.youtube.com/embed/Ths3o8X-XfM",
    videoWatchUrl: "https://www.youtube.com/watch?v=Ths3o8X-XfM",
    // Greyscale logos under the film — add a row to insert another achievement.
    founderLogosEyebrow: "Recognition for Kanchana Gupta",
    founderLogosHeading: "Recognised for opening access",
    founderLogos: [
        {
            src: "/revamp/images/brands/sg100-women-in-tech.jpg",
            greySrc: "/revamp/images/brands/sg100-women-in-tech-grey.png",
            alt: "Singapore 100 Women in Tech",
            href: "https://www.linkedin.com/posts/cheers-to-kanchana-gupta-for-being-recognised-ugcPost-7367159521342840833-TgWF/",
            label: "SG 100 Women in Tech",
            stack: true,
        },
        {
            src: "/revamp/images/brands/playpan.jpg",
            alt: "PlayPan Women with Impact",
            href: "https://www.linkedin.com/posts/iwd26-ugcPost-7435920234756349952-ux-Y/",
            label: "PlayPan Women with Impact",
        },
    ],
};
export const aboutBelief = {
    eyebrow: "Our belief",
    heading: "Every woman deserves access to someone who's walked the path before.",
    paras: [
        "By combining a global community with thoughtfully designed technology, we help create mentoring connections that might never have happened otherwise – across countries, industries, career stages, and backgrounds.",
        "Because when access expands, opportunities expand.",
    ],
    // Right side: photo collage from past events.
    collage: [
        { img: "/revamp/images/about-collage-1.webp", caption: "vLookUp t-shirts at an event" },
        { img: "/revamp/images/about-collage-2.webp", caption: "SGWIT cohort group photo" },
        { img: "/revamp/images/about-collage-3.webp", caption: "vLookUp community celebration" },
        { img: "/revamp/images/about-collage-4.webp", caption: "Speaker at a vLookUp event" },
    ],
};
// ==== v4.0 FINAL CONTENT DECK (July 2026 DOCX) ================================
// Section framing + CTAs that the finalized deck specifies verbatim. Pages should
// wire these in wherever the equivalent copy is currently hardcoded.
// ---- vLookUp Programmes: section framing ---------------------------------------
export const forOrgSectionHeads = {
    offer: {
        eyebrow: "What We Offer",
        heading: "End-to-end programme delivery: we handle everything",
        sub: "Not just a platform. We co-design, recruit mentors, manage matching, run the programme, and deliver impact reports.",
    },
    process: {
        eyebrow: "How It Works",
        heading: "From brief to impact in four steps",
    },
    tracks: {
        eyebrow: "Who We Serve",
        heading: "Two focused programme tracks",
        sub: "Whether you're growing leadership pipelines or supporting women entering the workforce, we have an approach for you.",
    },
    programmes: {
        eyebrow: "Our Track Record",
        heading: "Programmes in action",
    },
    why: {
        eyebrow: "The vLookUp Difference",
        heading: "Why organisations choose us",
    },
    gallery: {
        eyebrow: "In Action",
        heading: "Moments from our programmes",
    },
};
// Workshop highlight between How It Works and Who We Serve.
export const forOrgWorkshopStrip = {
    eyebrow: "Mentor Enablement Workshops",
    heading: "Build more confident, capable mentors",
    sub: "Already have mentors as part of your own programme? We can help equip them with the mindset, frameworks and practical tools to create more meaningful mentoring conversations.",
    label: "Clients",
    clients: ["LEGO Group", "MODEC", "Pernod Ricard India"],
    cta: { label: "Explore Mentor Enablement Workshops", href: "https://vlookup.ai/resources-details/workshops-turn-mentoring-experience-into-mentoring-impact" },
};
// "Also from vLookUp" cross-sell at the bottom of vLookUp Programmes.
export const forOrgCrossSell = {
    eyebrow: "Also from vLookUp",
    heading: "Looking for a self-serve mentoring platform?",
    sub: "MentorLookUp is our configurable SaaS product – deploy structured mentoring within your organisation or community, white-labelled and ready to launch quickly.",
    cta: { label: "Explore MentorLookUp", href: "/mentorlookup" },
};
export const forOrgFinalCta = {
    heading: "Ready to invest in your people?",
    sub: "Let's design a mentoring programme that moves the needle for your organisation.",
    primary: { label: "Reach Out Today", href: "mailto:support@vlookup.ai?subject=vLookUp%20Programmes%20enquiry" },
    // Flyer still to be produced — points at the Downloads shelf for now.
    secondary: { label: "Download Overview", href: "/revamp/images/vLookUpProgrammeFlyer.png" },
};
// ---- MentorLookUp: hero CTAs + section framing ---------------------------------
export const mluCtas = {
    primary: { label: "Book a Demo Today", href: "mailto:support@vlookup.ai?subject=MentorLookUp%20demo%20request" },
    secondary: { label: "See How It Works", href: "#how-it-works" },
};
export const mluSectionHeads = {
    whoFor: {
        eyebrow: "Who It's For",
        heading: "Mentoring technology built around your organisation",
        sub: "Whether you're connecting alumni with students, members with peers, or leaders with emerging talent – MentorLookUp gives you the infrastructure to do it well.",
    },
    features: {
        eyebrow: "Core Features",
        heading: "Everything you need to run mentoring at scale",
    },
    steps: {
        eyebrow: "Getting Started",
        heading: "From brief to live in four steps",
        sub: "We handle setup, configuration, and onboarding so you can launch quickly.",
    },
    reasons: {
        eyebrow: "Why MentorLookUp",
        heading: "Built by mentoring experts, for real programmes",
    },
    glance: {
        heading: "MentorLookUp at a glance",
    },
    cases: {
        eyebrow: "Client Platforms",
        heading: "We connect all levels, across the world",
        sub: "Organisations around the world use MentorLookUp to power student-alumni, peer-to-peer, and leadership mentoring programmes.",
        caseCta: "View platform",
    },
};
// "Also from vLookUp" cross-sell at the bottom of MentorLookUp.
export const mluCrossSell = {
    eyebrow: "Also from vLookUp",
    heading: "Looking for a delivered mentoring programme instead?",
    sub: "If you want vLookUp to design and run a bespoke women's mentoring programme for your organisation – we do that too.",
    cta: { label: "Explore for Organisations", href: "/for-organisations" },
};
export const mluFinalCta = {
    heading: "Let's build the future of mentoring together.",
    sub: "If you're looking to build a mentoring culture that is scalable, measurable, and deeply human – MentorLookUp is ready to partner with you.",
    primary: { label: "Book a Demo Today", href: "mailto:support@vlookup.ai?subject=MentorLookUp%20demo%20request" },
    // Flyer still to be produced — points at the Downloads shelf for now.
    secondary: { label: "Download Overview", href: "/revamp/images/MentorLookUpFlyer.png" },
};
// ---- Resources hub hero ---------------------------------------------------------
export const resourcesHero = {
    heading: "Resources Hub",
    sub: "Career insights, mentor spotlights, toolkits and programmes – all in one place.",
};
// ---- About: team framing + closing CTA ------------------------------------------
export const teamSection = {
    eyebrow: "The Team",
    heading: "Meet the people behind vLookUp",
    sub: "Committed diversity champions who truly believe in the power of diverse workplaces.",
};
export const aboutCta = {
    heading: "Ready to find your mentor – or become one?",
    sub: "Join thousands of women growing their careers through meaningful mentoring conversations.",
    primary: { label: "How it Works", href: "/how-it-works" },
    secondary: { label: "Join for Free", href: "/#join" },
};
// ---- Contact Us ----------------------------------------------------------------
export const contactPage = {
    title: "Contact us",
    description: "Would like to be a mentor or mentee with us? Queries? Concerns? Get in touch with us and we will be happy to assist you with your inquiries.",
    formTitle: "Quick enquiry",
    formSub: "Reach out to us for any inquiry",
    submit: "Submit",
    success: "Thank you for getting in touch. We'll get back to you as soon as we can.",
    image: "/revamp/images/fo-real-connections.webp",
    imageAlt: "Women connecting at a vLookUp mentoring programme event",
    locationLabel: "Locations",
    location: "Singapore",
    emailLabel: "Email:",
    email: "support@vlookup.ai",
};
